import { useMemo } from 'react';
import { isEmpty } from 'lodash';
import {
  organizationTreeSelector,
  selectTreeMap,
} from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import type { TOrganizationTreeNode } from '@timeedit/types';
import { TEInput } from '@timeedit/ui-components';
import { TreeSelect } from '@timeedit/ui-components/antd';

type TOrganizationTreeSelectProps = {
  value?: string;
  onChange?: (value: string) => void;
  label: string;
  tooltip?: string;
  placeholder?: string;
  dataTestId?: string;
  onlyLeafNodes?: boolean;
  allowClear?: boolean;
};

/**
 * Sames as {@link InlineOrganizationTreeSelect} but with label
 */
export const OrganizationTreeSelect = ({
  label,
  tooltip,
  ...props
}: TOrganizationTreeSelectProps) => {
  return (
    <TEInput.LabelWrapper label={label} tooltip={tooltip}>
      <InlineOrganizationTreeSelect {...props} />
    </TEInput.LabelWrapper>
  );
};

export const InlineOrganizationTreeSelect = ({
  value,
  onChange,
  placeholder,
  dataTestId,
  onlyLeafNodes,
  allowClear = false,
}: Omit<TOrganizationTreeSelectProps, 'label' | 'tooltip'>) => {
  const treeMap = useAppSelector(selectTreeMap);
  const organizationTree = useAppSelector(organizationTreeSelector);
  const treeData = useMemo<TTreeSelectOption[]>(() => {
    return (
      organizationTree?.children?.map((val) =>
        organizationTreeNodeToTreeSelectOption(val, !!onlyLeafNodes)
      ) ?? []
    );
  }, [onlyLeafNodes, organizationTree]);

  const displayValue = useMemo(() => {
    if (value == null || value === '') return undefined;
    return treeMap?.virtualIdsById[value]?.[0] ?? value;
  }, [value, treeMap]);

  return (
    <TreeSelect
      treeData={treeData}
      value={displayValue}
      treeNodeFilterProp="label"
      onChange={(virtualId) => {
        onChange?.(virtualId ? (treeMap?.byVirtualId[virtualId] ?? '') : '');
      }}
      showSearch
      popupMatchSelectWidth={false}
      allowClear={allowClear}
      placeholder={placeholder}
      data-testid={dataTestId}
    />
  );
};

type TTreeSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
  children?: Array<TTreeSelectOption>;
};

const organizationTreeNodeToTreeSelectOption = (
  node: TOrganizationTreeNode,
  onlyLeafNodes: boolean
): TTreeSelectOption => {
  const children = node.children?.map((val) =>
    organizationTreeNodeToTreeSelectOption(val, onlyLeafNodes)
  );
  return {
    value: node.virtualId,
    label: node.name,
    disabled: onlyLeafNodes && !isEmpty(children),
    children,
  };
};
